import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';
import { type App, failureMessage, request } from './service';

export default function Library({ mine = false }: { mine?: boolean }) {
  const { fetchJWT } = useAuthContext();
  const [search, setSearch] = useState('');
  const [missingOnly, setMissingOnly] = useState(false);
  const query = useQuery({
    queryKey: ['homebrew', 'apps'],
    queryFn: async () => {
      const { idToken } = await fetchJWT();
      if (!idToken) throw new Error('Sign in to view apps.');
      return request<{ apps: App[] }>(idToken);
    },
    retry: false,
  });
  const apps = (query.data?.apps || []).filter(
    (app) =>
      (!mine || app.needed) &&
      (!missingOnly || app.kind === 'missing') &&
      `${app.name} ${app.description} ${app.purpose}`
        .toLowerCase()
        .includes(search.toLowerCase().trim()),
  );
  return (
    <>
      <span className="govuk-caption-l">Moving to a MacBook</span>
      <h1 className="govuk-heading-xl">{mine ? 'My Apps' : 'App Library'}</h1>
      <p className="govuk-body-l">
        {mine
          ? 'Build your software list before you move. Find the apps you need, check Homebrew availability and record anything missing.'
          : 'See what colleagues need and what they have tried. Check install commands, known issues and gaps before your MacBook arrives.'}
      </p>
      <div className="homebrew-actions">
        <Link className="govuk-button" to="/homebrew/add">
          Find or add an app
        </Link>
        {mine && (
          <Link className="govuk-link" to="/homebrew/library">
            Browse App Library
          </Link>
        )}
      </div>
      <div className="govuk-form-group">
        <label className="govuk-label" htmlFor="library-search">
          Search {mine ? 'My Apps' : 'App Library'}
        </label>
        <input
          className="govuk-input"
          id="library-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="govuk-checkboxes govuk-checkboxes--small">
        <div className="govuk-checkboxes__item">
          <input
            className="govuk-checkboxes__input"
            type="checkbox"
            id="missing-only"
            checked={missingOnly}
            onChange={(e) => setMissingOnly(e.target.checked)}
          />
          <label className="govuk-label govuk-checkboxes__label" htmlFor="missing-only">
            Show apps without a Homebrew package
          </label>
        </div>
      </div>
      {query.isPending && (
        <p className="govuk-body" role="status">
          Loading apps...
        </p>
      )}
      {query.error && (
        <div role="alert">
          <p className="govuk-body">{failureMessage(query.error)}</p>
          <button
            type="button"
            className="govuk-button govuk-button--secondary"
            onClick={() => void query.refetch()}
          >
            Try again
          </button>
        </div>
      )}
      {query.data && (
        <p className="govuk-body" role="status">
          {apps.length} {apps.length === 1 ? 'app' : 'apps'}, most needed first
        </p>
      )}
      {query.data && apps.length === 0 && (
        <p className="govuk-body">
          No apps match this list. Browse the library or add software you need.
        </p>
      )}
      <ul className="govuk-list homebrew-app-list">
        {apps.map((app) => (
          <li key={app.id}>
            <div className="homebrew-app-heading">
              <h2 className="govuk-heading-m">
                <Link className="govuk-link" to={`/homebrew/apps/${app.id}`}>
                  {app.name}
                </Link>
              </h2>
              <strong className="govuk-tag govuk-tag--grey">
                {app.votes} {app.votes === 1 ? 'person needs' : 'people need'} this
              </strong>
            </div>
            <p className="govuk-body">{app.description || app.purpose}</p>
            <p className="govuk-body-s">
              {app.kind === 'missing'
                ? 'No Homebrew package linked - software gap recorded'
                : 'Homebrew package linked - open for current availability and reports'}
              {app.needed && ' · In My Apps'}
            </p>
          </li>
        ))}
      </ul>
    </>
  );
}
